/**
* Licensed Materials - Property of IBM
* IBM Cognos Products: Dashboard
* (C) Copyright IBM Corp. 2019
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/
define([
	'react',
	'react-dom',
	'prop-types',
	'ca-ui-toolkit',
	'dashboard-analytics/lib/@waca/core-client/js/core-client/ui/KeyCodes'
], function(React, ReactDOM, PropTypes, Toolkit, KeyCodes) {

	const iconpickerClassName = 'iconpicker';
	const { Flyout, SVGIcon, propsUtils, lodash } = Toolkit;
	const { sanitize } = propsUtils;
	const { uniqueId } = lodash;
	const { Component } = React;

	/**
	* @class IconPickerComponent
	* @classdesc React component to display a flyout containing selectable icons
	*/
	class IconPickerComponent extends Component {
		static propTypes = {
			/** Icon objects containing id and label to be displayed */
			icons: PropTypes.array.isRequired,
			/** Function that will get called when selecting an icon */
			onIconSelected: PropTypes.func,
			/** Function that will get called when flyout is closed */
			onClose: PropTypes.func,
			/** Flyout.propTypes.placement */
			orientation: Flyout.propTypes.placement,
			/** Custom class name(s) */
			className: PropTypes.string,
			/** Custom css styles */
			style: PropTypes.object,
			/** Trigger node to orient the flyout to. Defaults to the parent node of the flyout */
			triggerNode: PropTypes.object,
			/** Container node to place the flyout in */
			domNodeToAttachTo: PropTypes.object,
			/** Extra options to pass to Flyout */
			flyoutExtraOptions: PropTypes.object
		};

		static defaultProps = {
			icons: [],
			className: '',
			style: {},
			flyoutExtraOptions: {}
		}

		constructor(props) {
			super(props);

			this.state = { open: true };
			this._handleClose = this._handleClose.bind(this);
			this._handleSelect = this._handleSelect.bind(this);
			this._handleKey = this._handleKey.bind(this);
			this._id = uniqueId('icon-picker__');
		}

		render() {
			const { open } = this.state;
			const { className, style, flyoutExtraOptions, triggerNode, domNodeToAttachTo, ...others } = this.props;
			let orientation = this.props.orientation;
			if (typeof orientation === 'undefined') {
				orientation = (document.body.dir === 'rtl' ? 'bottomLeft' : 'bottomRight');
			}

			return (
				<div {...sanitize(others, IconPickerComponent)} className={`${iconpickerClassName} ${className}`} style={style}>
					{open &&
							<Flyout
								id={`${this._id}-flyout`}
								data-tid='icon-picker__flyout'
								{...flyoutExtraOptions}
								aria-hidden={open}
								placement={orientation}
								domNodeToAttachTo={domNodeToAttachTo}
								hideArrow
								offset={2}
								onClose={this._handleClose}
								className={`${iconpickerClassName}__flyout`}
								triggerNode={triggerNode}
							>
								{this.props.icons.map(this._displayIcon)}
							</Flyout>}
				</div>
			);
		}

		_displayIcon = (icon, index) => (
			<div key={index}
				tabIndex='0' // Make it keyboard focusable
				className={`${iconpickerClassName}__cell`}
				onClick={()=>this._handleSelect(icon)}
				onKeyDown={e=>this._handleKey(e, icon)}
				title={icon.label}
			>
				<SVGIcon
					iconId={icon.id}
					alt={icon.label}
					ariaLabelledby={uniqueId('icon-picker-svg')}
					verticalAlign={'inherit'}
				/>
			</div>
		)

		_handleClose() {
			this.setState((state) => ({ open: !state.open }));
			this.props.onClose && this.props.onClose();
		}

		_handleSelect(icon) {
			this.props.onIconSelected && this.props.onIconSelected(icon);
			// close the flyout after selection
			this.setState({ open: false });
		}

		_handleKey(e, icon) {
			if ((e.keyCode === KeyCodes.ENTER) || (e.keyCode === KeyCodes.SPACE)) {
				this._handleSelect(icon);
				// Prevent browser's default action: space does a PageDown
				e.preventDefault();
			}
		}
	}

	return IconPickerComponent;
});
